import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, linkedSignal, signal, untracked, viewChild } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { ContextMenuStateIndicatorComponent } from '@frontendlib/components/features/popover/components/context-menu-state-indicator/context-menu-state-indicator.component';
import { PopoverDirective } from '@frontendlib/components/features/popover/directives/popover.directive';
import { PanelBoardCreateComponent } from '@modules/boards/components/creation/panel-board-create/panel-board-create.component';
import { BoardsQuickFormComponent } from '@modules/boards/components/creation/boards-quick-form/boards-quick-form.component';
import { BoardsFolderPreviewComponent } from '@modules/boards/components/library/boards-folder-preview/boards-folder-preview.component';
import { BoardsFoldersListComponent } from '@modules/boards/components/library/boards-folders-list/boards-folders-list.component';
import { BOARD_VIEW_TITLES, BoardsListComponent } from '@modules/boards/components/library/boards-list/boards-list.component';
import { BoardsPreviewComponent } from '@modules/boards/components/library/boards-preview/boards-preview.component';
import { BoardsFoldersListContextMenuDirective } from '@modules/boards/components/menus/folders-list-context-menu.directive';
import { BoardsListContextMenuDirective } from '@modules/boards/components/menus/boards-list-context-menu.directive';
import { type BoardsPanelNavigation } from '@modules/boards/components/menus/boards-views-context-menu/boards-views-context-menu.component';
import { BoardsViewsContextMenuDirective } from '@modules/boards/components/menus/boards-views-context-menu.directive';
import { BoardsSearchPopoverComponent } from '@modules/boards/components/search/boards-search-popover/boards-search-popover.component';
import { BOARD_OPENER, type BoardOpenOptions } from '@modules/boards/components/shared/board-opener';
import { BoardListStore } from '@modules/boards/services/board-list.store';
import { BoardsPanelHandoffService } from '@modules/boards/services/boards-panel-handoff.service';
import { BoardsStore } from '@modules/boards/services/boards.store';
import { BoardsPanelStateService } from './boards-panel-state.service';
import { CommonModule as AppCommonModule } from '@modules/common/common.module';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { QuickActionButtonComponent } from '@modules/elements/components/quick-forms/quick-action-button/quick-action-button.component';
import { DynamicPanelService, type DynamicPanelBoardsTarget } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { SearchStateIndicatorComponent } from '@modules/search/components/search-state-indicator/search-state-indicator.component';
import { firstValueFrom, map, type Observable, of, Subject, tap, throwError } from 'rxjs';

/**
 * Boards in the dynamic panel (4th panel), ported line by line from `SlidesPanelComponent`: a view/folder picker, the filters and
 * search indicators, the "New board" bar, then the board list, or the folders (quick form, folder cards) and a folder's preview.
 * A board opens in the panel itself, in its record preview, with Back returning only to where it was opened from.
 * Its own `BoardListStore`, so filtering here leaves the Boards page as it is. The panel has no editor: "Open editor"
 * hands the board to the main page (`BoardsPanelHandoffService`), and nothing here ever begins or ends a full-window session.
 * Differences from Slides, each because Boards' counterpart works that way: unsaved Details are guarded by the sync
 * `canLeave()` (Slides' `prepareLeave()` is an Observable), and the folders-mode flag lives in `BoardListStore`
 * (persisted per host and space) instead of a separate panel-state service.
 */
@Component({
  selector: 'app-boards-panel',
  templateUrl: './boards-panel.component.html',
  styleUrls: ['../../../common/components/base-stitch/base-stitch.component.less', './boards-panel.component.less'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full' },
  providers: [
    BoardListStore,
    BoardsPanelStateService,
    {
      provide: BOARD_OPENER,
      useFactory: () => {
        const handoff = inject(BoardsPanelHandoffService);
        // The panel has no editor: its Open editor is the explicit-editor intent on the page.
        return (boardId: string, options?: BoardOpenOptions): void => void handoff.handOff({ kind: 'board', id: boardId, intent: 'editor', ...(options?.annotationThreadUid ? { annotationThreadUid: options.annotationThreadUid } : {}) });
      },
    },
  ],
  imports: [
    PanelBoardCreateComponent, AppCommonModule, BoardsFolderPreviewComponent, BoardsFoldersListComponent, BoardsQuickFormComponent,
    BoardsFoldersListContextMenuDirective, QuickActionButtonComponent,
    NgTemplateOutlet,
    Button2Component,
    IconComponent,
    MatTooltipModule,
    PopoverDirective,
    ContextMenuStateIndicatorComponent,
    SearchStateIndicatorComponent,
    BoardsListComponent,
    BoardsPreviewComponent,
    BoardsSearchPopoverComponent,
    BoardsListContextMenuDirective,
    BoardsViewsContextMenuDirective,
  ],
})
export class BoardsPanelComponent {
  protected readonly store = inject(BoardsStore);
  protected readonly listStore = inject(BoardListStore);
  private readonly dynamicPanel = inject(DynamicPanelService, { optional: true });
  private readonly spaceContext = inject(SpaceContextStore);
  private readonly toaster = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly preview = viewChild(BoardsPreviewComponent);
  private readonly folderPreview = viewChild(BoardsFolderPreviewComponent);
  private selectionRequestSequence = 0;
  private searchLeavePending = false;
  private returnFocus: HTMLElement | null = null;

  protected readonly folderSelectAllRequest = signal(0);
  private readonly panelState = inject(BoardsPanelStateService);
  private lastSpace = this.spaceContext.contextId();
  /** The open board follows the persisted panel navigation of the current space and account (`BoardsPanelStateService`):
   * a remount comes back to it, a space switch shows that space's own. */
  protected readonly openedBoardId = linkedSignal(() => this.panelState.state().openedBoardId);
  protected readonly openedFolderId = linkedSignal<string | null>(() => { this.spaceContext.contextId(); return null; });
  protected readonly openedFolder = computed(() => this.store.folders().find((folder) => folder.id === this.openedFolderId()) ?? null);
  protected readonly foldersOpen = this.listStore.foldersOpen;
  /** Whether the panel shows a board or a folder preview rather than a list. */
  private readonly showsItem = computed(() => !!this.openedBoardId() || !!this.openedFolder());
  protected readonly folderSearch = signal('');
  protected readonly folderFormOpen = signal(false);
  protected readonly folderCreationAllowed = computed(() => !['archived', 'deleted'].includes(this.listStore.view()));
  protected readonly viewsMenuOpened = signal(false);
  protected readonly popoverClose = new Subject<void>();
  /** Set when a board/folder was opened from a stitched list in a panel form: Back returns to that form. */
  protected readonly canGoBack = computed(() => this.dynamicPanel?.canGoBack() ?? false);
  /** Names where Back goes (the form the board was opened from), so it reads apart from the preview's own Close. */
  protected readonly backLabel = computed(() => {
    const title = this.dynamicPanel?.peekHistory()?.title;
    return title ? `Back to ${title}` : 'Go back';
  });

  protected readonly title = computed(() => {
    if (this.foldersOpen()) {
      if (this.listStore.view() === 'archived') return 'Archived folders';
      if (this.listStore.view() === 'deleted') return 'Trashed folders';
      return 'Folders';
    }
    const folderId = this.listStore.folderId();
    if (folderId) return this.store.folders().find((folder) => folder.id === folderId)?.title ?? 'Folder';
    return BOARD_VIEW_TITLES[this.listStore.view()];
  });

  /** Derived views (Flagged, Follow Up, Snoozed, Templates, Archive, Trash) offer no creation form, and neither does
   * Search Results, even once a folder has been picked: a board created there would match none of the search terms
   * that produced the list (devdoc/specs/quick-creation-forms.md). Same rule as Slides' panel. */
  protected readonly creationAllowed = computed(() => {
    if (this.foldersOpen() || this.openedFolder()) return false;
    const view = this.listStore.view();
    if (view === 'search_results') return false;
    return !!this.listStore.folderId() || view === 'all' || view === 'recent';
  });

  public constructor() {
    this.destroyRef.onDestroy(this.store.attachView());
    // Navigation persistence (Slides' `SlidesPanelStateService` use): the open board is written as it changes, Folders mode is restored
    // on the panel's OWN list store (never the main view's) and written back; a space switch restores that space's Folders mode.
    if (this.panelState.state().foldersOpen) this.listStore.showFolders(true);
    effect(() => {
      const id = this.openedBoardId();
      untracked(() => this.panelState.open(id));
    });
    effect(() => {
      const open = this.listStore.foldersOpen();
      const space = this.spaceContext.contextId();
      untracked(() => {
        if (space !== this.lastSpace) {
          this.lastSpace = space;
          if (this.panelState.state().foldersOpen) this.listStore.showFolders(true);
          return;
        }
        this.panelState.showFolders(open);
      });
    });
    effect(() => {
      const request = this.listStore.searchNavigationRequest();
      if (request) untracked(() => { void this.showSearchResults(); });
    });
    // A board or folder open in the panel holds the panel's item slot, so the panel's own app switch asks before dropping it.
    effect((onCleanup) => {
      if (!this.showsItem()) return;
      const release = untracked(() => this.dynamicPanel?.holdPanelItem());
      onCleanup(() => release?.());
    });
    const unregister = this.dynamicPanel?.registerSurfaceGuard(() => of(this.canLeave()));
    this.destroyRef.onDestroy(() => unregister?.());
    // A board or folder asked for from elsewhere is shown once this space's boards/folders are loaded; one asked for in another space is dropped.
    effect(() => {
      const target = this.dynamicPanel?.boardsTarget();
      if (!target) return;
      if (target.contextId !== this.spaceContext.contextId()) {
        untracked(() => this.dynamicPanel?.consumeBoardsTarget(target));
        return;
      }
      const loaded = target.kind === 'board' ? this.store.boardsLoaded() && !this.store.boardsLoading() : this.store.foldersLoaded() && !this.store.foldersLoading();
      if (!loaded) return;
      untracked(() => this.showBoardsTarget(target));
    });
    // A permanently deleted board leaves the panel on its list, not on a record with nothing to show.
    effect(() => {
      const id = this.openedBoardId();
      if (id && this.store.boardsLoaded() && !this.store.boardsLoading() && !this.store.boards().some((board) => board.id === id)) this.openedBoardId.set(null);
    });
  }

  /** False while an open board's Details or an open folder's form hold unsaved edits (each shows its own prompt). */
  private canLeave(): boolean {
    return (this.folderPreview()?.canLeave() ?? true) && (this.preview()?.canLeave() ?? true);
  }

  /** The "New board" bar's check, awaited before anything is created. */
  protected readonly beforeCreate = async (): Promise<boolean> => this.canLeave();

  protected createdBoard(id: string): void {
    this.listStore.ensureVisibleAfterCreate(id);
    // `beforeCreate` checked drafts when creation STARTED; the user can edit the open board's Details while the create
    // is pending, so recheck on completion. A veto keeps the created board in the list and offers Open.
    if (this.canLeave()) this.openedBoardId.set(id);
    else this.offerCreatedOpen(id);
  }

  private offerCreatedOpen(id: string): void {
    const space = this.spaceContext.contextId();
    this.toaster.show({ text: 'Board created', actions: [{ text: 'Open', handler: () => this.openGuarded(id, space) }] });
  }

  /** A notification's "Open" is deferred navigation: recheck the draft AND that the board still belongs to this space
   * (resolving a temp id the create may have reconciled) before switching. */
  private openGuarded(id: string, space = this.spaceContext.contextId()): void {
    if (this.destroyRef.destroyed || space !== this.spaceContext.contextId() || !this.canLeave()) return;
    const resolved = this.store.resolveBoardId(id);
    if (this.store.boards().some((board) => board.id === resolved)) this.openedBoardId.set(resolved);
  }

  protected open(id: string): void {
    if (!this.canLeave()) return;
    this.folderSelectAllRequest.set(0);
    this.returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.openedBoardId.set(id);
  }

  protected returnToList(): void {
    this.openedBoardId.set(null);
    requestAnimationFrame(() => { if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true }); });
  }

  protected leaveBoard(): void {
    if (this.preview()?.canLeave() !== false) this.returnToList();
  }

  protected goBack(): void {
    if (!this.canLeave()) return;
    this.dynamicPanel?.goBack();
  }

  /** The quick form files through this (native `createFromForm`): it closes on success; a refused or failed creation errors, keeping the draft. */
  protected readonly createFolderAction = (title: string): Observable<string> => {
    if (!this.folderCreationAllowed()) return throwError(() => new Error('Folders cannot be created here.'));
    return this.store.createFolder(title).pipe(
      tap((id) => { if (id) this.folderSearch.set(''); }),
      map((id) => { if (!id) throw new Error('The folder was not created.'); return id; }),
    );
  };

  /** The pending-save and dirty-Details check every search apply and clear goes through. */
  protected readonly mayLeave = (): boolean => this.canLeave();

  protected clearSearch(): void {
    if (this.canLeave()) this.listStore.clearSearch();
  }

  protected searchFolders(event: Event): void {
    if (event.target instanceof HTMLInputElement) this.folderSearch.set(event.target.value);
  }

  protected readonly navigateCollection = (view: BoardsPanelNavigation): void => {
    if (!this.canLeave()) return;
    this.openedBoardId.set(null);
    this.openedFolderId.set(null);
    if (view === 'folders') this.listStore.showFolders(true);
    else if (view === 'search_results') this.listStore.landOnSearchResults();
    else this.listStore.setView(view);
  };

  protected openFolder(id: string, selectAll = false): void {
    if (this.folderPreview()?.canLeave() === false) return;
    this.folderSelectAllRequest.set(selectAll ? ++this.selectionRequestSequence : 0);
    this.openedFolderId.set(id);
    this.listStore.setFolder(id);
  }

  private showBoardsTarget(target: DynamicPanelBoardsTarget): void {
    this.dynamicPanel?.consumeBoardsTarget(target);
    if (!this.canLeave()) return;
    if (this.foldersOpen()) this.listStore.setFolder(null);
    if (target.kind === 'boardFolder') {
      if (this.store.folders().some((folder) => folder.id === target.id)) {
        this.openedBoardId.set(null);
        this.openFolder(target.id);
      } else if (this.store.foldersLoadError()) {
        // A failed load leaves `folders()` empty or stale: that says nothing about whether the folder exists.
        this.toaster.show({ text: 'Could not load your folders. Try opening this folder again.' });
      } else this.toaster.show({ text: 'This folder is unavailable or you do not have access.' });
      return;
    }
    this.openedFolderId.set(null);
    if (this.store.boardsLoadError()) this.toaster.show({ text: 'Could not load your boards. Try opening this board again.' });
    else if (this.store.boards().some((board) => board.id === target.id && !board.deleted)) this.openedBoardId.set(target.id);
    else this.toaster.show({ text: 'This board is unavailable or you do not have access.' });
  }

  /** Search owns the parent list; leave its folder preview only after the draft decision. */
  private async showSearchResults(): Promise<void> {
    const folder = this.folderPreview();
    if (!folder || this.searchLeavePending) return;
    const folderId = this.openedFolderId();
    const space = this.spaceContext.contextId();
    this.searchLeavePending = true;
    try {
      const allowed = await firstValueFrom(folder.prepareLeave());
      if (!allowed || this.destroyRef.destroyed || space !== this.spaceContext.contextId() ||
          folderId !== this.openedFolderId() || this.listStore.view() !== 'search_results') return;
      this.openedFolderId.set(null);
    } finally {
      this.searchLeavePending = false;
    }
  }

  protected finishSearch(): void {
    this.closeSearchPopover();
    if (this.listStore.view() === 'search_results') void this.showSearchResults();
  }

  protected closeSearchPopover(): void {
    this.popoverClose.next();
  }
}
